// src/admin/companiesApi.js — the superadmin «Aziende» page (v212): list, read, a company's tenants and people,
// deactivate / activate, and the company purge (preview + purge). Contract:
// <BACKEND_FOLDER>/docs/api-server/endpoints.md § Companies and § Purge.
//
// ⛔ `suppressTenantHeader` on every call: these routes are scoped by the company id in the path.
// ⛔ Errors are buildApiError (err.code / err.status): worded by admin/adminErrorCopy.js, never err.message.
// ⚠️ A company whose purge STARTED answers 403 CUSTOMER_ADMIN_REQUIRED on read / tenants / people — superadmins
// included (BE dependencies.py `is_customer_admin_for`). `isPurgeStartedError` names it (decision C5). A superadmin
// can hold no company membership, so this 403 never raises the permissions-drift banner.
// ⚠️ The purge is ONE synchronous request (Stripe, every tenant, the people): it can take minutes (C6).
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

const CUSTOMERS = '/api/v1/admin/customers';
const PURGE = '/api/v1/admin/purge/customers';
export const PURGE_MODES = Object.freeze(['close', 'erase']);

// A missing id is a programming error: it throws before anything reaches the network.
const seg = (customerId) => {
  if (!customerId) throw new TypeError('companiesApi: a customer id is required');
  return encodeURIComponent(customerId);
};

const checkMode = (mode) => {
  if (!PURGE_MODES.includes(mode)) throw new TypeError(`companiesApi: unknown purge mode ${mode}`);
  return mode;
};

// FastAPI wraps a raised HTTPException detail in `{ detail: {...} }`; the handled envelope arrives flat.
async function call(path, init = {}, what = 'Request failed') {
  const res = await apiFetch(path, { ...init, suppressTenantHeader: true });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const envelope = body && typeof body.detail === 'object' && body.detail !== null ? body.detail : body;
    throw buildApiError(envelope, res.status, { fallbackMessage: `${what}: ${res.status}` });
  }
  return res.json();
}

// On the LIST `plan_id` and the seat fields are null (not loaded — never "none"); `workspace_count` counts every
// tenant, retired included. `withoutWorkspaces` alone lists ACTIVE orphans only (the filters AND together).
export function listCompanies({ q = '', includeInactive = false, withoutWorkspaces = false, limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const query = typeof q === 'string' ? q.trim() : '';
  if (query) params.set('q', query);
  if (includeInactive) params.set('include_inactive', 'true');
  if (withoutWorkspaces) params.set('without_workspaces', 'true');
  return call(`${CUSTOMERS}?${params}`, {}, 'Failed to list companies');
}

export const getCompany = (customerId) =>
  call(`${CUSTOMERS}/${seg(customerId)}`, {}, 'Failed to read company');

// Retired tenants included: decision C4 needs every one of them.
export const listCompanyTenants = (customerId) =>
  call(`${CUSTOMERS}/${seg(customerId)}/tenants?include_inactive=true`, {}, 'Failed to list company tenants');

export const listCompanyPeople = (customerId) =>
  call(`${CUSTOMERS}/${seg(customerId)}/users`, {}, 'Failed to list company people');

// Flag only: tenants stay active and runs are not blocked; admins lose their company rights at their next login.
export const deactivateCompany = (customerId) =>
  call(`${CUSTOMERS}/${seg(customerId)}/deactivate`, { method: 'POST' }, 'Failed to deactivate company');

export const activateCompany = (customerId) =>
  call(`${CUSTOMERS}/${seg(customerId)}/activate`, { method: 'POST' }, 'Failed to reactivate company');

// `mode` is required by the backend (a missing one is a 422).
export const previewCompanyPurge = (customerId, mode) =>
  call(`${PURGE}/${seg(customerId)}/preview?mode=${checkMode(mode)}`, {}, 'Failed to preview company purge');

// `confirm` must be the LOWERCASE id exactly (the backend compares with str(UUID)), so the canonical id is sent,
// never the operator's typing.
export const purgeCompany = (customerId, mode) =>
  call(`${PURGE}/${seg(customerId)}`, {
    method: 'POST',
    body: JSON.stringify({ confirm: String(customerId).toLowerCase(), mode: checkMode(mode) }),
  }, 'Failed to purge company');

export function isPurgeStartedError(err) {
  return err?.status === 403 && err?.code === 'CUSTOMER_ADMIN_REQUIRED';
}
